import { defineStore } from 'pinia';
import type {
  SolshareSystemSpec,
  InverterSpec,
  CTSpec,
  SolshareConfigResponse,
  TerminalAllocation,
} from '../models/SolshareSystemSpec';
import type { ConnectionFormData, TConnectionGroup } from '../models/Connection';
import type { SolshareSite } from '../models/Project';
import commissioningApiClient from '@/http/commissioningApiClient';
import { comApi } from '@/http/api';
import { ALGORITHM_TYPE } from '@/utils/constants';
import { getSolarDeliveryMethodFromAlgorithmType } from '@/shared/utils/solar-delivery-method';
import {
  allocationsToAlgorithmWeightArray,
  convertAllocationValues,
  getKwpAllocationPerPhase,
  ALLOCATION_UNIT,
} from '../utils/allocations';
import { transformConnectionsForSubmission } from '../utils/output-connections';
import { resolveHttpErrorMessage } from '@/http/http-error';
import { useCommissioningDeviceStore } from './commissioningDeviceStore';
import { COUNTRY_CODE } from '@/utils/constants';
import { isEmptyObject } from '@/utils/object';
import { getSolshareVersion } from '@/shared/utils/solshare-serial';
import { SOLSHARE_VERSION } from '@/shared/constants/solshare';
import {
  getInverterBrandOptions,
  INVERTER_BRAND_OTHER,
  isBatteryIntegrationSupported,
  findDefaultCTRating,
  isCTSpecStepRequired,
} from '../constants/config-constants';

/**
 * Purpose of useDeviceSpecificationStore:
 * - Stores user entered details (either retrieved from backend or UI draft),
 *   including project, address, and connection details.
 */
export const useDeviceSpecificationStore = defineStore('deviceSpecification', {
  persist: { omit: ['inverterSpecVersion', 'inverterSpecSaving'] },
  state: () => ({
    inverterSpecSerial: null as string | null,
    inverterSpecVersion: 0,
    inverterSpecSaving: false,
    solshareName: null as string | null,
    solshareSite: null as SolshareSite | null,
    solshareSystemSpec: {
      ctSpec: {} as CTSpec,
      inverterSpec: {} as InverterSpec,
      connections: [] as TConnectionGroup[],
    } as SolshareSystemSpec,
    draft: {
      allocationUnit: undefined as ALLOCATION_UNIT | undefined,
      connections: null as ConnectionFormData | null,
      algorithmType: '' as ALGORITHM_TYPE,
      allocations: {} as TerminalAllocation,
      selectedSite: null as SolshareSite | null,
    },
  }),

  getters: {
    getInverterBrand: (state) =>
      state.solshareSystemSpec.inverterSpec.otherBrand ??
      state.solshareSystemSpec.inverterSpec.brand,

    isProjectFromUK: (state) => state.solshareSite?.country === COUNTRY_CODE['United Kingdom'],

    /**
     * Checks whether the draft already has allocations.
     * If it does, it means the user has already configured them through the UI.
     * If not, the allocations must be initialized using
     * generateInitAllocations(...) before the user can start editing the allocations.
     *
     * @param state
     * @returns boolean
     */
    hasDraftAllocations: (state) => !isEmptyObject(state.draft.allocations),

    /**
     * Checks whether the entered SolShare has been assigned to a site.
     *
     * @returns boolean
     */
    hasAssignedSite: (state) =>
      state.solshareSite?.projectId &&
      state.solshareSite?.siteId &&
      state.solshareSite?.siteAddress,

    /**
     * Checks whether the user has selected a site to commission the SolShare.
     *
     * @returns boolean
     */
    hasSelectedSite: (state) =>
      state.draft.selectedSite?.projectId &&
      state.draft.selectedSite?.siteId &&
      state.draft.selectedSite?.siteAddress,

    /**
     * Determines the site to configure the SolShare.
     * User selected site takes precedence over the backend retrieved site.
     *
     * @returns SolshareSite | null
     */
    siteToConfigure(): SolshareSite | null {
      return this.hasSelectedSite ? this.draft.selectedSite : this.solshareSite;
    },
  },

  actions: {
    beginInverterSpecContext(serial: string) {
      this.inverterSpecVersion++;
      this.inverterSpecSaving = false;
      if (this.inverterSpecSerial !== serial) {
        const spec = this.solshareSystemSpec.inverterSpec;
        delete spec.inverterSerialNumber;
        delete spec.batteryCapacity;
        if (getSolshareVersion(serial) === SOLSHARE_VERSION.Mk2) {
          delete spec.brand;
          delete spec.otherBrand;
        }
      }
      this.inverterSpecSerial = serial;
    },

    isInverterSpecContextCurrent(serial: string, version: number): boolean {
      return (
        useCommissioningDeviceStore().serialNumber === serial &&
        this.inverterSpecSerial === serial &&
        this.inverterSpecVersion === version
      );
    },

    /**
     * Retrieve all solshare info from the backend.
     * @param serial
     * @returns
     */
    async refreshSolshareConfigFromBackend(serial: string) {
      if (useCommissioningDeviceStore().serialNumber !== serial) return;
      if (this.inverterSpecSerial !== serial) this.beginInverterSpecContext(serial);
      const hydrateInverterSpec = !this.inverterSpecSaving;
      const version = hydrateInverterSpec ? ++this.inverterSpecVersion : this.inverterSpecVersion;
      const getConfigEndpoint = comApi.getSolshareConfig(serial);
      try {
        const response = await commissioningApiClient.get(getConfigEndpoint);
        if (
          !this.isInverterSpecContextCurrent(serial, version) ||
          response.data.serialNumber !== serial
        )
          return;
        this.__updateSolshareSystemSpec(response.data, hydrateInverterSpec);
        this.__resetDraftConnections(); // reset the draft so the user can start from scratch
      } catch (error: unknown) {
        if (!this.isInverterSpecContextCurrent(serial, version)) return;
        const errorMessage = resolveHttpErrorMessage(error, 'Failed to retrieve SolShare config');
        throw new Error(errorMessage);
      }
    },

    /** Update the solshare system spec in the store. */
    __updateSolshareSystemSpec(data: SolshareConfigResponse, hydrateInverterSpec = true) {
      const { spec, connections, address } = data;

      this.__updateSolshareSite(address);
      if (hydrateInverterSpec) this.__updateAcDcSpec(spec, data.serialNumber);
      this.__updateConnections(connections);

      return;
    },

    __updateSolshareSite(projectInfo: SolshareConfigResponse['address']) {
      this.solshareName = projectInfo.solshareName ?? '';
      this.solshareSite = {
        projectId: projectInfo.projectId ?? null,
        projectName: projectInfo.projectName ?? null,
        country: projectInfo.country ?? null,
        siteId: projectInfo.siteId ?? null,
        siteAddress: projectInfo.address,
        sitePostcode: projectInfo.postcode ?? null,
      } as SolshareSite;
    },

    __updateAcDcSpec(spec: SolshareConfigResponse['spec'], serial: string) {
      if (getSolshareVersion(serial) === SOLSHARE_VERSION.Mk2) {
        const option = getInverterBrandOptions(this.solshareSite?.country as COUNTRY_CODE).find(
          (option) => option.value === spec.inverter,
        );
        // False without zero-capacity capture preserves an Other selection matching a brand.
        const useOther =
          !!spec.inverter &&
          (!option ||
            (option.battery_integration_supported &&
              spec.need_battery_integration === false &&
              spec.battery_capacity !== 0));
        const captureVisible = option?.battery_integration_supported && !useOther;
        this.solshareSystemSpec.inverterSpec = {
          systemSize: Number(spec.dcSize),
          capacity: Number(spec.acSize),
          brand: useOther ? INVERTER_BRAND_OTHER : option?.value,
          otherBrand: useOther ? spec.inverter : undefined,
          inverterSerialNumber: captureVisible ? spec.inverter_serial_number : undefined,
          batteryCapacity:
            captureVisible && Number.isFinite(spec.battery_capacity)
              ? spec.battery_capacity
              : undefined,
        };
        return;
      }
      // MK1 keeps the existing locally selected brand; its API still returns an empty string.
      this.solshareSystemSpec.inverterSpec = {
        ...this.solshareSystemSpec.inverterSpec,
        systemSize: Number(spec.dcSize),
        capacity: Number(spec.acSize),
      };
    },

    __updateConnections(connections: SolshareConfigResponse['connections']) {
      this.solshareSystemSpec.connections = connections;
    },

    __resetDraftConnections() {
      this.draft = {
        ...this.draft,
        connections: null,
        algorithmType: '' as ALGORITHM_TYPE,
        allocations: {} as TerminalAllocation,
        allocationUnit: undefined,
      };
    },

    async resetDraftAllocations() {
      this.draft.allocations = {} as TerminalAllocation;
      this.draft.allocationUnit = undefined;
    },

    async setInverterSpec(serial: string, inverterSpec: InverterSpec): Promise<boolean> {
      if (getSolshareVersion(serial) === SOLSHARE_VERSION.Mk2) {
        if (
          !this.isInverterSpecContextCurrent(serial, this.inverterSpecVersion) ||
          this.inverterSpecSaving
        )
          return false;
        const supported = isBatteryIntegrationSupported(
          inverterSpec.brand,
          this.solshareSite?.country as COUNTRY_CODE,
        );
        const snapshot: InverterSpec = {
          systemSize: inverterSpec.systemSize,
          capacity: inverterSpec.capacity,
          brand: inverterSpec.brand,
          otherBrand:
            inverterSpec.brand === INVERTER_BRAND_OTHER ? inverterSpec.otherBrand : undefined,
          inverterSerialNumber: supported ? inverterSpec.inverterSerialNumber : undefined,
          batteryCapacity: supported ? inverterSpec.batteryCapacity : undefined,
        };
        const version = ++this.inverterSpecVersion;
        this.inverterSpecSaving = true;
        try {
          await commissioningApiClient.post(comApi.uploadSpecs(serial), {
            serialNumber: serial,
            spec: {
              dcSize: snapshot.systemSize,
              acSize: snapshot.capacity,
              inverter:
                snapshot.brand === INVERTER_BRAND_OTHER ? snapshot.otherBrand : snapshot.brand,
              need_battery_integration: supported && snapshot.batteryCapacity !== 0,
              ...(supported
                ? {
                    inverter_serial_number: snapshot.inverterSerialNumber,
                    battery_capacity: snapshot.batteryCapacity,
                  }
                : {}),
            },
          });
          if (!this.isInverterSpecContextCurrent(serial, version)) return false;
          this.solshareSystemSpec.inverterSpec = snapshot;
          this.inverterSpecSaving = false;
          this.inverterSpecVersion++;
          return true;
        } catch (error: unknown) {
          if (!this.isInverterSpecContextCurrent(serial, version)) return false;
          throw new Error(
            resolveHttpErrorMessage(error, 'Failed to upload inverter specification'),
          );
        } finally {
          if (this.isInverterSpecContextCurrent(serial, version)) this.inverterSpecSaving = false;
        }
      }
      try {
        this.solshareSystemSpec.inverterSpec = inverterSpec;
        await commissioningApiClient.post(comApi.uploadSpecs(serial), {
          serialNumber: serial,
          spec: {
            dcSize: inverterSpec.systemSize,
            acSize: inverterSpec.capacity,
            inverter: this.getInverterBrand,
          },
        });
        return true;
      } catch (error: unknown) {
        const errorMessage = resolveHttpErrorMessage(
          error,
          'Failed to upload inverter specification',
        );
        throw new Error(errorMessage);
      }
    },

    async setCommissionConfig(serial: string, ctSpec: CTSpec) {
      this.solshareSystemSpec.ctSpec = ctSpec;
    },

    updateSelectedSite(selectedSiteDetails: SolshareSite) {
      this.draft.selectedSite = selectedSiteDetails;
    },

    /**
     * Save the meter.siteId to the backend.
     *
     * If has user selected site, use the user selected siteId.
     * Else, re-save with the site details that were retrieved from the backend earlier.
     *
     * @param serial
     */
    async saveMeterSiteId(serial: string) {
      const siteDetailsToSave = this.siteToConfigure ?? ({} as SolshareSite);
      if (!siteDetailsToSave.projectId || !siteDetailsToSave.siteId) {
        throw new Error('No site details available.');
      }

      try {
        await commissioningApiClient.patch(comApi.updateMeter(serial), {
          siteId: siteDetailsToSave.siteId,
        });
        this.solshareSite = siteDetailsToSave;
        this.clearSelectedSite();
      } catch (error: unknown) {
        const errorMessage = resolveHttpErrorMessage(error, 'Failed to save meter site ID');
        throw new Error(errorMessage);
      }
    },

    clearSelectedSite() {
      this.draft.selectedSite = null;
    },

    updateDraftConnections(newDraft: ConnectionFormData) {
      this.draft.connections = newDraft;
    },

    updateDraftAlgorithmType(newDraft: ALGORITHM_TYPE) {
      this.draft.algorithmType = newDraft;
    },

    updateDraftTerminalAllocation(newDraft: TerminalAllocation) {
      this.draft.allocations = newDraft;
    },

    updateDraftAllocationUnit(newDraft: ALLOCATION_UNIT) {
      this.draft.allocationUnit = newDraft;
    },

    async uploadConnections(serial: string) {
      if (this.draft.connections === null) {
        throw new Error('No connections data available.');
      }

      try {
        const commissioningDeviceStore = useCommissioningDeviceStore();
        const transformedConnections = transformConnectionsForSubmission(
          this.draft.connections,
          commissioningDeviceStore.outputConnections,
        );

        await commissioningApiClient.post(comApi.uploadConnection(serial), {
          connections: transformedConnections,
        });

        await this.refreshSolshareConfigFromBackend(serial); // refetch the saved connection data to update the store
      } catch (error: unknown) {
        const errorMessage = resolveHttpErrorMessage(error, 'Failed to upload connections');
        throw new Error(errorMessage);
      }
    },

    /**
     * The API call to POST /upload-config will save the following in DB, but also triggers the config upload to SolShare, which put the SolShare in commissioning state.
     *
     * This api call will save these in DB:
     * - CTRating (this is the only API to upload ct rating; MK2 always submits the default rating)
     * - solarDeliveryMethod (derieved from algorithm type)
     * - algorithmWeight
     *
     * BUT we needed to include the following in the payload due to the API requirements:
     * - serialNumber
     * - address { address }
     * - connections
     * - spec { dcSize, acSize, inverter }
     */
    async uploadAllCommissionConfig(serial: string) {
      let draftAllocations = this.draft.allocations;

      // Before saving to database, convert allocations from kWp to percentage if the allocation unit is kWp
      if (this.draft.allocationUnit === ALLOCATION_UNIT.kWp) {
        const baseKwp = this.solshareSystemSpec.inverterSpec.systemSize ?? 0;
        const kwpPerPhase = getKwpAllocationPerPhase(serial, baseKwp);
        draftAllocations = convertAllocationValues(
          draftAllocations,
          ALLOCATION_UNIT.kWp, // fromUnit
          ALLOCATION_UNIT.percentage, // toUnit
          kwpPerPhase,
        );
      }
      const commissioningDeviceStore = useCommissioningDeviceStore();

      // The CT spec step is skipped for MK2, so always submit the default rating (ignores any persisted value).
      const CTRating = isCTSpecStepRequired(serial)
        ? (this.solshareSystemSpec.ctSpec.rating ?? '')
        : findDefaultCTRating();

      const firstTimeSubmit = {
        CTRating,
        solarDeliveryMethod: getSolarDeliveryMethodFromAlgorithmType(this.draft.algorithmType),
        connectedTerminals: commissioningDeviceStore.connectedTerminalsArray,
        algorithmWeight:
          this.draft.algorithmType === ALGORITHM_TYPE.DEMAND_BASED_DELIVERY
            ? []
            : allocationsToAlgorithmWeightArray(draftAllocations),
      };

      const previouslySubmittedData = {
        serialNumber: serial,
        address: {
          solshareName: this.solshareName,
          address: this.solshareSite?.siteAddress,
          country: this.solshareSite?.country,
        },
        connections: this.solshareSystemSpec.connections,
        spec: {
          dcSize: this.solshareSystemSpec.inverterSpec.systemSize,
          acSize: this.solshareSystemSpec.inverterSpec.capacity,
          inverter: this.getInverterBrand,
        },
      };

      const payload = {
        ...previouslySubmittedData,
        ...firstTimeSubmit,
      };

      try {
        await commissioningApiClient.post(comApi.uploadConfig(), payload);
      } catch (error: unknown) {
        const errorMessage = resolveHttpErrorMessage(error, 'Failed to submit commission config');
        throw new Error(errorMessage);
      }
    },
  },
});
